/** @jsxImportSource ./simboluri-jsx */
import { useState } from "react";
import { SeismDirectie, SeismResult, StepState, TorsiuneNivel, fmtDate } from "./tipuri";
import ModeleRigiditate from "./ModeleRigiditate";
import TextSimboluri, { S } from "./Simbol";
import Info from "./Info";
import { DescriereaPasului } from "./TitluPas";
import Marime, { EtichetaNatura } from "./Marime";
import { t, useT } from "../i18n";

// Pasul 9 — Analiza seismică: spectrul de proiectare P100-1, răspunsul
// modal combinat CQC pe 4 direcții (0°/45°/90°/135°) + torsiunea
// accidentală. Cazurile rezultate (E_0..T_accY) se adaugă în tensorul de
// eforturi al Pasului 8, pentru combinațiile din Pasul 11.

export default function StepSeism({
  state,
  running,
}: {
  state: StepState | null;
  running: boolean;
}) {
  useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.seism.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.seism.construieste_spectrul_pr")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.seism.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="seism" />
      </div>
    );
  }

  const r = state.result as unknown as SeismResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.pas.esuat", { eroare: r?.error ?? t("struct.pas.eroare_necunoscuta") })}
        </div>
        <div className="sg-muted">{t("struct.seism.ruleaza_nou_meniul")}</div>
      </div>
    );
  }

  const p = r.parametri;
  const cm = r.criteriu_masa_modala;
  const dirX = r.directii.find((d) => d.directie_deg === 0);

  return (
    <div className="st-result">
      <ModeleRigiditate pas={9} r={r} />
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.reg.acceleratia_proiectare")}
            {/* explicația vine din catalog, deci e aceeași cu cea de pe
                cardul Pasului 1 — era scrisă local aici și lipsea acolo */}
            <EtichetaNatura cheie="a_g" />
            <Marime cheie="a_g" extra={<>{t("struct.seism.pe_proiectul_acesta", { sursa: p.sursa_ag })}</>} />
            <Marime cheie="Tc" />
          </div>
          <div className="st-card-value">{p.ag_g} g</div>
          <div className="st-card-sub">
            {p.ag_ms2} m/s² · {p.sursa_ag}
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">{t("struct.seism.perioada_fundamentala_x")}<EtichetaNatura cheie="T1" /><Marime cheie="T1" /></div>
          <div className="st-card-value">
            {dirX ? `${dirX.T_fundamental_s} s` : "—"}
          </div>
          <div className="st-card-sub">
            {dirX
              ? t("struct.seism.sd_t1_mod", { sd: dirX.sd_fundamental_ms2, mod: dirX.mod_fundamental })
              : ""}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.seism.cazuri_seismice_adaugate")}<Marime cheie="cazuri_seismice" /></div>
          <div className="st-card-value">{r.cazuri_adaugate.length}</div>
          <div className="st-card-sub">
            {t("struct.seism.intra_combinatii_p11", { lista: r.cazuri_adaugate.join(", ") })}
          </div>
        </div>
      </div>

      <div className="st-findings">
        <h3>
          {t("struct.seism.parametrii_spectrului")}
          <Info
            titlu={t("struct.seism.spectrul_p100_1")}
            ce={t("struct.seism.curba_s_d")}
            deCe={t("struct.seism.structurile_rigide_t")}
            parametri={[
              { simbol: "TB, TC, TD", nume: t("struct.seism.perioadele_colt_spectrul"), sursa: t("struct.seism.tc_pasul_1") },
              { simbol: "β0", nume: t("struct.seism.amplificarea_maxima_2"), sursa: "P100-1" },
              { simbol: "γI,e", nume: t("struct.seism.factorul_importanta_seis"), sursa: t("struct.seism.clasa_importanta_a") },
              { simbol: "ξ", nume: t("struct.seism.amortizarea_critica_core"), sursa: t("struct.seism.constanta_a_metodei") },
            ]}
          />
        </h3>
        <table className="sg-table">
          <tbody>
            <tr>
              <td><S k="a_g">a_g</S> {t("struct.seism.acceleratia_terenului")}</td>
              <td className="sg-mono">{p.ag_g} g = {p.ag_ms2} m/s²</td>
              <td className="sg-muted"><TextSimboluri text={p.sursa_ag} /></td>
            </tr>
            <tr>
              <td>
                <S k="Tc">T_B</S> / <S k="Tc">T_C</S> / <S k="Tc">T_D</S>{" "}
                {t("struct.seism.perioadele_de_colt")}
              </td>
              <td className="sg-mono">{p.tb_s} / {p.tc_s} / {p.td_s} s</td>
              <td className="sg-muted">
                {t("struct.seism.tc_din_zonare")}
              </td>
            </tr>
            <tr>
              <td>{t("struct.seism.0_amplificarea_maxima")}</td>
              <td className="sg-mono">{p.beta0}</td>
              <td className="sg-muted">{t("struct.seism.p100_1_constant")}</td>
            </tr>
            <tr>
              <td>{t("struct.seism.q_factorul_comportare")}</td>
              <td className="sg-mono">{p.q}</td>
              <td className="sg-muted">{t("struct.seism.constanta_presupus_armare")}</td>
            </tr>
            <tr>
              <td>{t("struct.seism.i_factorul_importanta")}</td>
              <td className="sg-mono">{p.gamma_ie}</td>
              <td className="sg-muted">{t("struct.seism.clasa_importanta_a")}</td>
            </tr>
            <tr>
              <td>{t("struct.seism.amortizarea_critica")}</td>
              <td className="sg-mono">{p.xi}</td>
              <td className="sg-muted">{t("struct.seism.constanta_cqc")}</td>
            </tr>
            <tr>
              <td>{t("struct.seism.numarul_moduri_considera")}</td>
              <td className="sg-mono">{p.nr_moduri}</td>
              <td className="sg-muted">
                {cm
                  ? t("struct.seism.retinute_p1_extrase", { p1: cm.nr_moduri_extrase, p2: cm.nr_moduri_min_cerut })
                  : t("struct.seism.din_analiza_modala_p7")}
              </td>
            </tr>
            {cm && (
              <tr>
                <td>{t("struct.seism.masa_efectiva_cumulata2")}</td>
                <td className={cm.criteriu_90_ok ? "sg-mono" : "sg-mono st-warn"}>
                  {cm.meff_cumulat_pct["0"]}% / {cm.meff_cumulat_pct["90"]}%
                </td>
                <td className="sg-muted">
                  {t(cm.criteriu_90_ok ? "struct.seism.criteriu_90_indeplinit" : "struct.seism.criteriu_90_neindeplinit", {
                    n: cm.nr_moduri_min_cerut,
                    x: cm.meff_la_nr_moduri_min_pct["0"],
                    y: cm.meff_la_nr_moduri_min_pct["90"],
                  })}
                </td>
              </tr>
            )}
            {p.solver_liniar && (
              <tr>
                <td>{t("struct.seism.solverul_liniar")}</td>
                <td className="sg-mono">{p.solver_liniar}</td>
                <td className="sg-muted">
                  {t("struct.seism.lu_rar_multifrontal")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="st-findings">
        <h3>
          {t("struct.seism.spectrul_proiectare_sd")}
          <Info
            titlu={t("struct.seism.citeste_graficul")}
            ce={t("struct.seism.orizontala_perioada_t")}
            deCe={t("struct.seism.daca_t1_cade")}
          />
        </h3>
        <SpectruChart r={r} />
      </div>

      <div className="st-findings">
        <h3>
          {t("struct.seism.raspunsul_pe_directii")}
          <Info
            titlu={t("struct.seism.4_directii")}
            ce={t("struct.seism.cutremurul_vine_dintr")}
            deCe={t("struct.seism.testul_t7_compara")}
            parametri={[
              { simbol: t("struct.seism.simbol_v_baza"), nume: t("struct.seism.forta_taietoare_baza"), sursa: t("struct.seism.calculata_aici") },
              { simbol: "F_b", nume: t("struct.seism.forta_statica_echivalent"), sursa: t("struct.seism.meff_sd_metoda_a") },
              { simbol: "m_eff", nume: t("struct.seism.masa_efectiva_cumulata"), sursa: t("struct.seism.tinta_90_p100") },
            ]}
          />
        </h3>
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.seism.directie")}</th>
              <th>T₁</th>
              <th>{t("struct.fem.col_mod")}</th>
              <th>S_d(T₁)</th>
              <th>{t("struct.seism.meff_cumulat")}</th>
              <th>{t("struct.seism.v_baza")}</th>
              <th>{t("struct.seism.fb_echiv")}</th>
              <th>V / F_b</th>
              <th>T7</th>
            </tr>
          </thead>
          <tbody>
            {r.directii.map((d) => (
              <RandDirectie key={d.directie_deg} d={d} />
            ))}
          </tbody>
        </table>
      </div>

      <ModuriTabel r={r} />

      <div className="st-findings">
        <h3>
          {t("struct.seism.torsiunea_accidentala")}
          <Info
            titlu={t("struct.seism.momentele_torsiune_accid")}
            ce={t("struct.seism.masa_reala_niciodata")}
            deCe={t("struct.seism.excentricitatea_accident")}
            parametri={[
              { simbol: "F_i", nume: t("struct.seism.forta_seismica_a"), sursa: t("struct.seism.calculata_aici") },
              { simbol: "e1", nume: t("struct.seism.excentricitatea_accident2"), sursa: "P100-1 §4.5.2.1" },
            ]}
          />
        </h3>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          {(["X", "Y"] as const).map((dir) => (
            <div key={dir} style={{ flex: "1 1 280px" }}>
              <div className="st-small" style={{ marginBottom: 6 }}>{t("struct.arpl.directia_dir", { dir })}</div>
              <TorsiuneTabel rows={r.torsiune_accidentala[dir]} />
            </div>
          ))}
        </div>
      </div>

      <div className="sg-muted">{r.nota}</div>
      <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}</div>
    </div>
  );
}

function RandDirectie({ d }: { d: SeismDirectie }) {
  const esec = d.t7_ok === false;
  return (
    <tr style={esec ? { fontWeight: 600, color: "#b5323d" } : {}}>
      <td className="sg-mono">{d.directie_deg}°</td>
      <td className="sg-mono">{d.T_fundamental_s} s</td>
      <td className="sg-mono">{d.mod_fundamental}</td>
      <td className="sg-mono">{d.sd_fundamental_ms2} m/s²</td>
      <td className="sg-mono">{d.meff_cumulat_pct}%</td>
      <td className="sg-mono">{d.v_baza_kn} kN</td>
      <td className="sg-mono">{d.f_b_echivalent_kn} kN</td>
      <td className="sg-mono">{d.raport_v_fb ?? "—"}</td>
      <td>
        {d.t7_ok === null ? (
          <span className="sg-muted">{t("struct.rzv.informativ")}</span>
        ) : d.t7_ok ? (
          <span className="st-ok">✓</span>
        ) : (
          <span className="st-bad">✗</span>
        )}
      </td>
    </tr>
  );
}

function TorsiuneTabel({ rows }: { rows: TorsiuneNivel[] }) {
  return (
    <table className="sg-table">
      <thead>
        <tr>
          <th>{t("struct.fem.mc_nivel")}</th>
          <th>F_i</th>
          <th>e1</th>
          <th>Mt</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t, i) => (
          <tr key={i}>
            <td className="sg-mono">{t.nivel}</td>
            <td className="sg-mono">{t.F_i_kn} kN</td>
            <td className="sg-mono">{t.e1_m} m</td>
            <td className="sg-mono">{t.Mt_knm} kN·m</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Tabelul modurilor (primele 10 + expandare)
// ---------------------------------------------------------------------------

function ModuriTabel({ r }: { r: SeismResult }) {
  useT();
  const [toate, setToate] = useState(false);
  const moduri = toate ? r.moduri : r.moduri.slice(0, 10);
  return (
    <div className="st-findings">
      <h3>
        {t("struct.seism.acceleratia_spectrala_mod")}
        <Info
          titlu={t("struct.seism.s_d_t")}
          ce={t("struct.seism.mod_propriu_are")}
          deCe={t("struct.seism.raspunsul_total_combinar")}
        />
      </h3>
      <table className="sg-table fem-modes">
        <thead>
          <tr>
            <th>{t("struct.fem.col_mod")}</th>
            <th>T (s)</th>
            <th>S_d (m/s²)</th>
          </tr>
        </thead>
        <tbody>
          {moduri.map((m) => (
            <tr key={m.mod}>
              <td className="sg-mono">{m.mod}</td>
              <td className="sg-mono">{m.T_s}</td>
              <td className="sg-mono">{m.sd_ms2}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {r.moduri.length > 10 && (
        <button className="md-tab" style={{ marginTop: 8 }} onClick={() => setToate(!toate)}>
          {toate ? t("struct.seism.arata_primele_10") : t("struct.seism.arata_cele_p1", { p1: r.moduri.length })}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Graficul spectrului — SVG simplu (polyline), fără librării
// ---------------------------------------------------------------------------

function SpectruChart({ r }: { r: SeismResult }) {
  useT();
  const W = 680;
  const H = 280;
  const padL = 52;
  const padR = 16;
  const padT = 14;
  const padB = 38;

  const spectru = r.spectru;
  if (!spectru || spectru.length === 0) {
    return <div className="sg-muted">{t("struct.seism.spectrul_disponibil_rezu")}</div>;
  }

  const tMax = Math.max(...spectru.map((s) => s.T_s));
  const sdMax = Math.max(...spectru.map((s) => s.sd_ms2));
  const x = (t: number) => padL + (t / tMax) * (W - padL - padR);
  const y = (sd: number) => H - padB - (sd / sdMax) * (H - padT - padB);

  const puncte = spectru.map((s) => `${x(s.T_s).toFixed(1)},${y(s.sd_ms2).toFixed(1)}`).join(" ");

  // marcaje verticale la perioadele fundamentale (deduplicate)
  const marcaje: { T: number; label: string }[] = [];
  r.directii.forEach((d) => {
    const gasit = marcaje.find((m) => Math.abs(m.T - d.T_fundamental_s) < 1e-4);
    if (gasit) gasit.label += `/${d.directie_deg}°`;
    else marcaje.push({ T: d.T_fundamental_s, label: `${d.directie_deg}°` });
  });

  // gradațiile axelor (valori „rotunde")
  const tickT: number[] = [];
  const pasT = tMax > 2 ? 0.5 : 0.25;
  for (let t = 0; t <= tMax + 1e-9; t += pasT) tickT.push(Number(t.toFixed(2)));
  const tickSd: number[] = [];
  const pasSd = sdMax > 4 ? 1 : 0.5;
  for (let s = 0; s <= sdMax + 1e-9; s += pasSd) tickSd.push(Number(s.toFixed(2)));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", maxWidth: W, background: "#fff", border: "1px solid #dedede", borderRadius: 6 }}
      role="img"
      aria-label={t("struct.seism.aria_spectrul")}
    >
      {/* grila + gradațiile */}
      {tickSd.map((s) => (
        <g key={`s${s}`}>
          <line x1={padL} y1={y(s)} x2={W - padR} y2={y(s)} stroke="#dedede" />
          <text x={padL - 6} y={y(s) + 3} textAnchor="end" fontSize="10" fill="#737373">
            {s}
          </text>
        </g>
      ))}
      {tickT.map((t) => (
        <g key={`t${t}`}>
          <line x1={x(t)} y1={padT} x2={x(t)} y2={H - padB} stroke="#ededed" />
          <text x={x(t)} y={H - padB + 14} textAnchor="middle" fontSize="10" fill="#737373">
            {t}
          </text>
        </g>
      ))}

      {/* axele */}
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#888" />
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#888" />
      <text x={(padL + W - padR) / 2} y={H - 6} textAnchor="middle" fontSize="11" fill="#3a3a3a">
        T (s)
      </text>
      <text
        x={14}
        y={(padT + H - padB) / 2}
        textAnchor="middle"
        fontSize="11"
        fill="#3a3a3a"
        transform={`rotate(-90 14 ${(padT + H - padB) / 2})`}
      >
        S_d (m/s²)
      </text>

      {/* perioadele fundamentale */}
      {marcaje.map((m, i) => (
        <g key={i}>
          <line
            x1={x(m.T)}
            y1={padT}
            x2={x(m.T)}
            y2={H - padB}
            stroke="#b5323d"
            strokeWidth="1"
            strokeDasharray="4 3"
          />
          <text
            x={x(m.T) + 3}
            y={padT + 10 + i * 12}
            fontSize="10"
            fill="#b5323d"
          >
            T₁ {m.label} = {m.T} s
          </text>
        </g>
      ))}

      {/* curba spectrului */}
      <polyline points={puncte} fill="none" stroke="#3d7cab" strokeWidth="2" />
    </svg>
  );
}
